import { createContext, useContext, useEffect, useState } from "react";
import { api, ApiError } from "../lib/api.ts";
import type { DealListPage, DealMonthsPage, DealRow, DealSlice, Filters } from "../lib/api.ts";
import { money, monthEnd, monthLabel, num } from "../lib/format.ts";
import { dbLabel, plural, t, type L } from "../i18n/lang.ts";
import { UI } from "../i18n/dict.ts";
import { Th, useSortable, type SortColumn } from "./sortable.tsx";
import type { DrillDown } from "./leadList.tsx";
import type { ConversationDrill } from "./conversationList.tsx";
import type { ExceptionDrill, PartyCheckDrill } from "./exceptionList.tsx";
import type { TicketDrill } from "./ticketSources.tsx";
import type { PaymentDrill } from "./depositReview.tsx";
import type { B2bDrill } from "../lib/b2bApi.ts";
import { DrawerActions } from "./exportButton.tsx";
import { exportToExcel, fetchAllPages, moneyValue, type ExportColumn } from "../lib/tableExport.ts";

/**
 * Список СДЕЛОК под цифрой, с переходом в карточку сделки и в карточку лида.
 *
 * ПОЧЕМУ НЕ КОПИЯ ПАНЕЛИ ЛИДОВ. У сделки другие колонки (сумма, депозит,
 * остаток, дата праздника), две ссылки вместо одной и колонка расхождений. Но
 * главное — сделка бывает БЕЗ ЛИДА: замер 10.09.2026 на периоде 01.07-10.09,
 * эпоха managed, — в блоке «Требует внимания» 24 сделки из 105 не имеют лида
 * вовсе, а в строке «выиграна, но депозит не записан» их 11 из 13. Панель
 * лидов под этой плиткой показала бы 2 строки под числом 13.
 *
 * ЗАГОЛОВОК НАЗЫВАЕТ ДАТУ ОТБОРА, и это не украшение. Три плитки экрана
 * «Конверсия воронки» стоят на трёх разных датах: 403 сделки по моменту
 * закрытия депозита, 231 по дате праздника, 230 по дате закрытия. Без подписи
 * все три списка получили бы одинаковые заголовки, и человек, открывший второй,
 * решил бы, что видит тот же список.
 */

/** Проза этой панели. Один блок в начале файла — см. App.tsx. */
const TX = {
  dealsCount: {
    one: { ru: "сделка", en: "deal" },
    few: { ru: "сделки", en: "deals" },
    many: { ru: "сделок", en: "deals" },
  },
  noMatch: { ru: "Под этот срез сделок нет.", en: "No deals match this slice." },
  colEventMonth: { ru: "Месяц праздника", en: "Event month" },
  dealsHeader: { ru: "Сделок", en: "Deals" },
  noEventDate: { ru: "дата праздника не заполнена", en: "no event date" },
  backToMonths: { ru: "все месяцы", en: "all months" },
  colCreated: { ru: "Создана", en: "Created" },
  colOwner: { ru: "Ответственный", en: "Owner" },
  colEvent: { ru: "Праздник", en: "Event" },
  colAmount: { ru: "Сумма", en: "Amount" },
  colBalance: { ru: "Остаток", en: "Balance" },
  colIssues: { ru: "Расхождения", en: "Issues" },
  /* Хвост подписи стадии, когда стадия отчитывается под другим именем. */
  reportsAs: { ru: "в отчётах —", en: "reports as" },
  paidOn: { ru: "оплачено", en: "paid" },
  noLead: { ru: "без лида", en: "no lead" },
  /* --- по какой дате срезан список */
  basisCreate: { ru: "по дате создания сделки", en: "by the date the deal was created" },
  /* День праздника с исключениями сверки — так считают плитки завершённых. */
  basisParty: {
    ru: "по дню праздника, с исключениями сверки",
    en: "by the day of the party, reconciliation exceptions applied",
  },
  /* Подпись под исправленным значением: что стоит в самой сделке. */
  inCrm: { ru: "в CRM:", en: "in the CRM:" },
  /* --- колонки выгрузки в Excel: то, что в таблице стоит второй строкой ячейки */
  exDealId: { ru: "ID сделки", en: "Deal ID" },
  exBooking: { ru: "Бронь", en: "Booking" },
  exPaid: { ru: "Оплачено", en: "Paid" },
  exEventCrm: { ru: "Праздник в CRM", en: "Event in the CRM" },
  exAmountCrm: { ru: "Сумма в CRM", en: "Amount in the CRM" },
  exAmountNative: { ru: "Сумма в валюте парка", en: "Amount in park currency" },
  exCurrency: { ru: "Валюта", en: "Currency" },
  exLeadId: { ru: "ID лида", en: "Lead ID" },
  exLeadPhone: { ru: "Телефон лида", en: "Lead phone" },
  exReportStage: { ru: "Стадия в отчётах", en: "Reports as" },
  exDealLink: { ru: "Ссылка на сделку", en: "Deal link" },
  exLeadLink: { ru: "Ссылка на лид", en: "Lead link" },
  /* Тот же базис, но сутки по времени парка: так считает дайджест, и
     разница в четыре часа обязана быть написана, а не угадываться. */
  basisCreateLocal: {
    ru: "по дате создания сделки, сутки по времени парка",
    en: "by the date the deal was created, in park-local days",
  },
  basisClosedate: { ru: "по дате закрытия сделки", en: "by the date the deal was closed" },
  basisEvent: { ru: "по дате праздника", en: "by the date of the event" },
  basisBooked: { ru: "по дате закрытия депозита", en: "by the date the deposit was closed" },
  basisPaid: {
    ru: "по дате поступления денег",
    en: "by the date the money arrived",
  },
  basisDeposit: {
    ru: "по дате оплаты депозита в системе бронирования",
    en: "by the date the deposit was paid in the booking system",
  },
  basisDepositReview: {
    ru: "по дате оплаты, иначе входа в Booked, иначе создания",
    en: "by the payment date, else the move to Booked, else creation",
  },
  /* Строка сделки в списках депозитов: когда депозит засчитан и откуда дата. */
  depositOn: { ru: "депозит", en: "deposit" },
  depositNotConfirmed: { ru: "депозит не подтверждён оплатой", en: "deposit not confirmed by a payment" },
  bookedOn: { ru: "в Booked", en: "booked" },
  duplicateOf: { ru: "дубль сделки", en: "duplicate of deal" },
  exDepositAt: { ru: "Депозит засчитан", en: "Deposit counted on" },
  exBookedAt: { ru: "Вход в Booked", en: "Moved to Booked" },
  exDuplicateOf: { ru: "Дубль сделки", en: "Duplicate of deal" },
  /* Депозит сделки, взятый из оплаты брони, потому что поле CRM пустое (миграция 100). */
  depositFromBooking: { ru: "из брони", en: "from booking" },
  exDepositFromBooking: { ru: "Депозит из брони", en: "Deposit from booking" },
  yes: { ru: "да", en: "yes" },
  /* --- листалка */
  firstPage: { ru: "⇤ В начало", en: "⇤ First" },
  previousPage: { ru: "← Назад", en: "← Previous" },
  nextPage: { ru: "Далее →", en: "Next →" },
  pageOf: { ru: "Страница", en: "Page" },
  goToPage: { ru: "Перейти на страницу", en: "Go to page" },
} satisfies Record<string, L | { one: L; few: L; many: L }>;

/**
 * Расхождения, коротко. Ключ — элемент `issue_flags` с сервера.
 *
 * Формулировки короткие: это значок в строке таблицы, а не подпись плитки.
 * Полные подписи плиток приходят с сервера по-английски (блок attention), и
 * дублировать их здесь незачем — человек уже пришёл из плитки и знает, что
 * искал; ему нужно увидеть ВТОРОЕ расхождение той же сделки.
 */
const ISSUE_LABEL: Record<string, L> = {
  won_without_amount: { ru: "нет суммы", en: "no amount" },
  won_without_deposit: { ru: "нет депозита", en: "no deposit" },
  deposit_on_lost: { ru: "депозит на проигрыше", en: "deposit on a lost deal" },
  deposit_over_amount: { ru: "депозит больше суммы", en: "deposit over amount" },
  open_past_event: { ru: "праздник прошёл", en: "event has passed" },
  no_event_date: { ru: "нет даты праздника", en: "no event date" },
  deposit_unconfirmed: { ru: "нет оплаты", en: "no payment" },
  deposit_paid_wrong_stage: { ru: "оплачена, не в Booked", en: "paid, not booked" },
  duplicate_open: { ru: "дубль", en: "duplicate" },
  park_from_booking: { ru: "парк из брони", en: "park from booking" },
};

/**
 * Исключения сделки, коротко — значок в строке. Полное имя и причина — в
 * панели исключений; здесь важно увидеть, что строка посчитана не по CRM.
 */
const EXCEPTION_LABEL: Record<string, L> = {
  duplicate: { ru: "дубль", en: "duplicate" },
  reserve_deleted: { ru: "резерв удалён", en: "reserve deleted" },
  no_party_check: { ru: "нет чека", en: "no check" },
  joint_party: { ru: "совместный праздник", en: "joint party" },
  party_date: { ru: "дата по чеку", en: "date from the check" },
  held_not_won: { ru: "прошёл, не закрыта", en: "held, not won" },
};

const BASIS_LABEL: Record<DealSlice["dateBasis"], L> = {
  create: TX.basisCreate,
  party: TX.basisParty,
  create_local: TX.basisCreateLocal,
  closedate: TX.basisClosedate,
  event: TX.basisEvent,
  booked: TX.basisBooked,
  paid: TX.basisPaid,
  deposit: TX.basisDeposit,
  deposit_review: TX.basisDepositReview,
};

/**
 * Колонки списка. На уровне модуля, потому что доступ должен быть виден хуку,
 * который вызывается до раннего выхода компонента.
 *
 * Денежные колонки возвращают `*_minor`, а не то, что напечатала `money()`:
 * «436К» и «91 200» как строки несравнимы. Подпись сделки повторяет формулу
 * ячейки — `#id` вместо пустого заголовка, — иначе сделка без названия встала бы
 * между настоящими значениями.
 */
const DEAL_COLS: Record<
  "deal" | "created" | "stage" | "park" | "owner" | "event" | "amount" | "deposit" | "balance" | "lead",
  SortColumn<DealRow>
> = {
  deal: { kind: "text", get: (r) => (r.title === null || r.title === "" ? `#${r.deal_id}` : r.title) },
  /* Даты приходят строкой `YYYY-MM-DD` — текстовая колонка упорядочивает их
     верно, сегменты выровнены по позициям. */
  created: { kind: "text", get: (r) => r.created },
  stage: { kind: "text", get: (r) => r.stage_name },
  park: { kind: "text", get: (r) => r.park_name },
  owner: { kind: "text", get: (r) => r.manager_name },
  event: { kind: "text", get: (r) => r.date_of_event },
  amount: { kind: "number", get: (r) => r.opportunity_aed_minor },
  deposit: { kind: "number", get: (r) => r.deposit_aed_minor },
  balance: { kind: "number", get: (r) => r.balance_aed_minor },
  /* Прочерк у сделки без лида обязан дать `null`, иначе она встанет между
     настоящими подписями. */
  lead: { kind: "text", get: (r) => (r.lead_id === null ? null : (r.lead_title ?? `#${r.lead_id}`)) },
};

/**
 * Колонки выгрузки в Excel — те же, что у таблицы, в том же порядке. Вторая
 * строка ячейки (бронь, «в CRM: …», сумма в своей валюте, телефон лида) уходит
 * отдельной колонкой рядом. Под плитками завершённых праздников день и сумма —
 * с исключениями сверки, как на экране, а значение из CRM — соседней колонкой.
 */
function dealExportColumns(
  partyBasis: boolean,
  depositView = false,
  /** Выгрузка сразу за все месяцы разбивки: месяц праздника — отдельной колонкой, чтобы в Excel по нему фильтровать. */
  withEventMonth = false,
): Array<ExportColumn<DealRow>> {
  const cols: Array<ExportColumn<DealRow>> = [
    { title: t(UI.deal), width: 36, value: (r) => (r.title === null || r.title === "" ? `#${r.deal_id}` : r.title) },
    { title: t(TX.exDealId), kind: "number", width: 10, value: (r) => r.deal_id },
    { title: t(TX.exBooking), width: 12, value: (r) => r.booking_ref },
    { title: t(TX.exPaid), kind: "datetime", width: 17, value: (r) => r.booking_paid_at },
    { title: t(TX.colCreated), kind: "date", width: 12, value: (r) => r.created },
    { title: t(UI.park), width: 24, value: (r) => r.park_name },
    { title: t(TX.colOwner), width: 22, value: (r) => r.manager_name },
    { title: t(TX.colEvent), kind: "date", width: 12, value: (r) => (partyBasis ? r.party_date : r.date_of_event) },
  ];
  if (partyBasis) cols.push({ title: t(TX.exEventCrm), kind: "date", width: 13, value: (r) => r.date_of_event });
  if (withEventMonth) {
    cols.push({
      title: t(TX.colEventMonth),
      width: 16,
      value: (r) => {
        const d = partyBasis ? r.party_date : r.date_of_event;
        return d ? monthLabel(d.slice(0, 7)) : t(TX.noEventDate);
      },
    });
  }
  cols.push({
    title: t(TX.colAmount),
    kind: "money",
    width: 12,
    value: (r) => moneyValue(partyBasis ? r.party_amount_aed_minor : r.opportunity_aed_minor),
  });
  if (partyBasis) cols.push({ title: t(TX.exAmountCrm), kind: "money", width: 13, value: (r) => moneyValue(r.opportunity_aed_minor) });
  cols.push(
    {
      title: t(TX.exAmountNative),
      kind: "money",
      width: 14,
      value: (r) => (r.currency !== null && r.currency !== "AED" ? moneyValue(r.opportunity_minor, r.currency) : null),
    },
    { title: t(TX.exCurrency), width: 8, value: (r) => r.currency },
    { title: t(UI.deposit), kind: "money", width: 12, value: (r) => moneyValue(r.deposit_aed_minor) },
    { title: t(TX.exDepositFromBooking), width: 10, value: (r) => (r.deposit_from_booking === true ? t(TX.yes) : null) },
    { title: t(TX.colBalance), kind: "money", width: 12, value: (r) => moneyValue(r.balance_aed_minor) },
    {
      title: t(TX.colIssues),
      width: 30,
      value: (r) =>
        [
          ...r.issue_flags.map((f) => dbLabel(ISSUE_LABEL, f, f)),
          ...r.exception_kinds.map((k) => dbLabel(EXCEPTION_LABEL, k, k)),
        ].join("; "),
    },
    {
      title: t(UI.lead),
      width: 28,
      value: (r) => (r.lead_id === null ? t(TX.noLead) : r.lead_title === null || r.lead_title === "" ? `#${r.lead_id}` : r.lead_title),
    },
    { title: t(TX.exLeadId), kind: "number", width: 10, value: (r) => r.lead_id },
    { title: t(TX.exLeadPhone), width: 16, value: (r) => r.lead_phone_e164 },
    { title: t(UI.stage), width: 20, value: (r) => r.stage_name },
    {
      title: t(TX.exReportStage),
      width: 18,
      value: (r) => (r.report_stage_name !== null && r.report_stage_name !== r.stage_name ? r.report_stage_name : null),
    },
    { title: t(TX.exDealLink), width: 50, value: (r) => r.bitrix_url },
    { title: t(TX.exLeadLink), width: 50, value: (r) => r.lead_bitrix_url },
  );
  /* Списки депозитов: когда депозит засчитан, когда сделка вошла в Booked и
     чей она дубль — ровно то, по чему её отобрали. */
  if (depositView) {
    cols.splice(
      4,
      0,
      { title: t(TX.exDepositAt), kind: "datetime", width: 17, value: (r) => r.deposit_at },
      { title: t(TX.exBookedAt), kind: "datetime", width: 17, value: (r) => r.booked_at },
      { title: t(TX.exDuplicateOf), kind: "number", width: 10, value: (r) => (r.duplicate_of === null ? null : Number(r.duplicate_of)) },
    );
  }
  return cols;
}

/** Что показывает список сделок. Те же поля, что у `DrillDown` для лидов. */
export type DealDrill = {
  title: string;
  /** Пояснение под заголовком: по какому именно условию собран список. */
  hint?: string;
  slice: DealSlice;
  /**
   * Открывать НЕ списком, а разбивкой по месяцам праздника.
   *
   * Нужно там, где число собрано по дате получения денег, а праздники по этим
   * деньгам расходятся на полгода вперёд: под «закрытыми депозитами» человеку
   * нужен ответ «сколько из них сентябрь», а не полторы сотни строк подряд.
   * Список никуда не девается — он открывается вторым шагом, по щелчку на месяц.
   */
  byEventMonth?: boolean;
  /**
   * Своё окно вместо периода с верхней панели.
   *
   * Нужно экранам, которые period сверху НЕ применяют. Утренний дайджест
   * считает одни сутки; без этой правки список под его плиткой «1» показал
   * бы все сделки выбранного периода — то есть другое число под тем же
   * заголовком. Поля накладываются на текущие фильтры, поэтому парки и
   * бренды остаются теми же, что на экране.
   */
  filters?: Partial<Filters>;
};

/**
 * Детализация под плиткой — лиды, сделки или диалоги открытой линии.
 *
 * `kind` у ветки лидов НЕОБЯЗАТЕЛЕН, и это сделано ради существующих вызовов:
 * `ChannelTable` и таблицы маркетинга уже зовут `onDrill({ title, slice })` без
 * всякого признака, и требование дописать `kind` тронуло бы четыре чужих файла
 * ради ничего. Различение при этом остаётся строгим: `drill.kind === "deals"`
 * сужает тип, потому что у остальных веток значение обязательное.
 *
 * Третья ветка — диалоги. Заводить её пришлось потому, что у обращения без
 * ответа объект детализации не запись CRM: владельцем диалога бывает и лид, и
 * сделка, и контакт, и список лидов под таким числом был бы короче его самого.
 */
export type Drill =
  | ({ kind?: "leads" } & DrillDown)
  | ({ kind: "deals" } & DealDrill)
  | ({ kind: "conversations" } & ConversationDrill)
  | ({ kind: "checks" } & PartyCheckDrill)
  | ({ kind: "exceptions" } & ExceptionDrill)
  | ({ kind: "tickets" } & TicketDrill)
  | ({ kind: "payments" } & PaymentDrill)
  /* Сделки воронки B2B — своя панель (b2b.tsx): у них своя таблица и свои поля. */
  | ({ kind: "b2b" } & B2bDrill);

/**
 * Кто открывает детализацию. Через контекст, а не пропсом сверху вниз.
 *
 * Причина в объёме: плиток `Kpi` на экранах около сорока, и они разбросаны по
 * пяти файлам экранов. Проп-открывашка означала бы, что каждый экран и каждая
 * промежуточная карточка тянут `onDrill` через свою сигнатуру — пять чужих
 * файлов правки ДО того, как обвяжется первая плитка. С контекстом обвязка
 * плитки — одна строка `drill={{ … }}`, а открывашка объявлена один раз в
 * `App.tsx`, где и живёт состояние панели.
 *
 * `null` значит «открывать нечем»: плитка с `drill` остаётся ровно такой, какой
 * была, а не рисует кнопку, которая ничего не делает.
 */
const DrillContext = createContext<((d: Drill) => void) | null>(null);

export function DrillProvider({
  open,
  children,
}: {
  open: (d: Drill) => void;
  children: React.ReactNode;
}): React.ReactElement {
  return <DrillContext.Provider value={open}>{children}</DrillContext.Provider>;
}

export function useDrillOpener(): ((d: Drill) => void) | null {
  return useContext(DrillContext);
}

/** Сколько строк за раз. Потолок сервера — 500. */
const PAGE = 200;

export function DealDrawer({
  drill,
  filters,
  onClose,
}: {
  drill: DealDrill | null;
  filters: Filters;
  onClose: () => void;
}): React.ReactElement | null {
  const [page, setPage] = useState<DealListPage | null>(null);
  const [months, setMonths] = useState<DealMonthsPage | null>(null);
  /** Выбранный месяц праздника. `null` — стоим на первой ступени. */
  const [month, setMonth] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  /** Что человек НАБРАЛ в поле номера страницы, до подтверждения. */
  const [typed, setTyped] = useState("");

  /**
   * Срез с учётом выбранного месяца.
   *
   * Месяц выражается уже существующими полями окна праздника — никакого нового
   * параметра не понадобилось. Поэтому второй шаг это тот же список, что и
   * раньше, просто с более узким срезом.
   */
  const effective: DealSlice | null =
    drill === null
      ? null
      : month === null
        ? drill.slice
        : { ...drill.slice, eventFrom: `${month}-01`, eventTo: monthEnd(month) };

  const key = drill === null ? "" : JSON.stringify([effective, filters]);
  /** Первая ступень нужна только когда её попросили И месяц ещё не выбран. */
  const onMonths = drill?.byEventMonth === true && month === null;

  /** Смена среза возвращает на первую страницу: иначе откроется пустой хвост. */
  useEffect(() => {
    setOffset(0);
    setTyped("");
  }, [key]);

  /** Новая плитка — новая детализация: месяц от предыдущей не переносится. */
  useEffect(() => {
    setMonth(null);
  }, [drill]);

  useEffect(() => {
    if (drill === null || effective === null) return;
    let alive = true;
    setLoading(true);
    setError(null);
    const request = onMonths
      ? api.dealMonths(filters, effective).then((r) => {
          if (alive) setMonths(r);
        })
      : api.deals(filters, effective, PAGE, offset).then((r) => {
          if (alive) setPage(r);
        });
    request
      .catch((e: unknown) => {
        if (alive) setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [key, offset, onMonths]);

  /** Escape закрывает — панель модальная, и мышь до крестика тянуть не нужно. */
  useEffect(() => {
    if (drill === null) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drill, onClose]);

  const rows = onMonths ? [] : (page?.rows ?? []);
  const total = onMonths ? (months?.total ?? 0) : (page?.total ?? 0);
  const monthRows = months?.rows ?? [];

  /**
   * Сортировка включается ТОЛЬКО когда весь срез уместился на одной странице.
   *
   * Список страничный, и порядок задаёт сервер: по дате праздника — вперёд (это
   * расписание), по остальным датам — назад. Отсортировать страницу по сумме
   * значило бы напечатать сверху «самую крупную сделку» из двухсот, стоящих в
   * середине среза, — не неудобство, а неверное число в отчёте. Своего
   * параметра сортировки у `/api/deals` нет, поэтому механизму передаётся
   * только `partial`: заголовки перестают быть кнопками и объясняют причину
   * подсказкой. `onSort` появится здесь в тот день, когда появится на сервере, —
   * и тогда `partial` останется, а заголовки снова оживут.
   *
   * Условие ровно то же, по которому ниже появляется листалка, так что «видна
   * листалка — сортировки нет» читается без объяснений.
   */
  const S = useSortable(rows, DEAL_COLS, { partial: total > rows.length });

  if (drill === null) return null;

  const shown = offset + rows.length;
  const pages = Math.max(1, Math.ceil(total / PAGE));
  const current = Math.floor(offset / PAGE) + 1;

  /** Прыжок по номеру: за пределы среза не пускаем — там пустая страница. */
  const jump = (): void => {
    const n = Number(typed);
    setTyped("");
    if (!Number.isFinite(n)) return;
    const clamped = Math.min(Math.max(Math.trunc(n), 1), pages);
    setOffset((clamped - 1) * PAGE);
  };

  const basis = t(BASIS_LABEL[drill.slice.dateBasis]);
  /* Под плитками завершённых праздников день и сумма — с исключениями, и
     исправленное значение стоит первым, а то, что в CRM, — подписью под ним. */
  const partyBasis = drill.slice.dateBasis === "party";
  /* Списки депозитов (миграция 097): под названием — дата депозита и дубль. */
  const depositView = drill.slice.dateBasis === "deposit" || drill.slice.dateBasis === "deposit_review";

  /**
   * Выгрузка — весь список под цифрой.
   *
   * На разбивке по месяцам («Закрытые депозиты») выгружаются ВСЕ месяцы сразу —
   * та же совокупность, что в числе на плитке, с месяцем праздника отдельной
   * колонкой. До 23.10.2026 кнопки здесь не было вовсе, и общий файл
   * собирался руками из помесячных. Внутри месяца — только этот месяц.
   *
   * Если весь срез уже на экране, берётся он — в том порядке, в каком его
   * отсортировали; иначе строки собираются со всех страниц.
   */
  const exportTitle = month === null ? drill.title : `${drill.title} · ${monthLabel(month)}`;
  const runExport =
    effective === null
      ? undefined
      : async (progress: (done: number, total: number) => void): Promise<void> => {
          const all =
            !onMonths && total <= rows.length
              ? S.rows
              : await fetchAllPages((limit, off) => api.deals(filters, effective, limit, off), progress);
          await exportToExcel(exportTitle, dealExportColumns(partyBasis, depositView, onMonths), all);
        };

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-head">
          <div>
            <div className="drawer-title">
              {drill.title}
              {month !== null && (
                <>
                  {" · "}
                  {monthLabel(month)}{" "}
                  <button type="button" className="chip-btn" onClick={() => setMonth(null)}>
                    {t(TX.backToMonths)}
                  </button>
                </>
              )}
            </div>
            <div className="drawer-hint">
              {loading && page === null
                ? t(UI.loading)
                : `${num(total)} ${plural(total, TX.dealsCount)}`}
              {` · ${basis}`}
              {drill.hint ? ` · ${drill.hint}` : ""}
            </div>
          </div>
          <DrawerActions onClose={onClose} exportRun={runExport} exportDisabled={loading || total === 0} />
        </div>

        {/* Содержимое отделено от липкой шапки: панель выезжает справа во весь
            рост экрана, и шапка должна оставаться на месте при прокрутке. */}
        <div className="drawer-body">
          {error !== null && <div className="error">{error}</div>}

          {error === null && !loading && (onMonths ? monthRows.length === 0 : rows.length === 0) && (
            <div className="empty">{t(TX.noMatch)}</div>
          )}

          {/* ПЕРВАЯ СТУПЕНЬ: месяцы праздника. Щелчок по строке сужает срез
              окном праздника и открывает уже обычный список. */}
          {onMonths && monthRows.length > 0 && (
            <div className="scroll-x">
              <table className="data">
                <thead>
                  <tr>
                    <th>{t(TX.colEventMonth)}</th>
                    <th className="num">{t(TX.dealsHeader)}</th>
                    <th className="num">{t(UI.deposit)}</th>
                    <th className="num">{t(TX.colAmount)}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {monthRows.map((m) => (
                    <tr key={m.month ?? "none"}>
                      <td>
                        {m.month === null ? (
                          t(TX.noEventDate)
                        ) : (
                          <button
                            type="button"
                            className="link-num"
                            onClick={() => setMonth(m.month)}
                          >
                            {monthLabel(m.month)}
                          </button>
                        )}
                      </td>
                      <td className="num">{num(m.deals)}</td>
                      <td className="num">{money(m.deposit_aed_minor, "AED", { withCode: false })}</td>
                      <td className="num">{money(m.amount_aed_minor, "AED", { withCode: false })}</td>
                      <td />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {rows.length > 0 && (
            <div className="scroll-x">
              <table className="data">
                <thead>
                  <tr>
                    <Th sort={S} col="deal">{t(UI.deal)}</Th>
                    <Th sort={S} col="created">{t(TX.colCreated)}</Th>
                    <Th sort={S} col="park">{t(UI.park)}</Th>
                    <Th sort={S} col="owner">{t(TX.colOwner)}</Th>
                    <Th sort={S} col="event">{t(TX.colEvent)}</Th>
                    <Th sort={S} col="amount" num>{t(TX.colAmount)}</Th>
                    <Th sort={S} col="deposit" num>{t(UI.deposit)}</Th>
                    <Th sort={S} col="balance" num>{t(TX.colBalance)}</Th>
                    {/* Расхождения — набор значков, сортировать нечем. */}
                    <th>{t(TX.colIssues)}</th>
                    <Th sort={S} col="lead">{t(UI.lead)}</Th>
                    {/* Стадия — в самом конце. Заказчик на разборе цифр читал её
                        первой и путался: «что такое deal won и approved booked».
                        Для депозита стадия сделки не ответ на вопрос «взяли ли
                        деньги», поэтому она ушла из начала строки в хвост. */}
                    <Th sort={S} col="stage">{t(UI.stage)}</Th>
                    {/* Кнопка перехода в Битрикс — сортировать нечего. */}
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {S.rows.map((r) => (
                    <tr key={r.deal_id}>
                      <td>
                        <a href={r.bitrix_url} target="_blank" rel="noreferrer">
                          {r.title === null || r.title === "" ? `#${r.deal_id}` : r.title}
                        </a>
                        {r.booking_ref !== null && (
                          <div className="sub">
                            {r.booking_ref}
                            {r.booking_paid_at !== null && ` · ${t(TX.paidOn)} ${r.booking_paid_at}`}
                          </div>
                        )}
                        {depositView && (
                          <div className="sub">
                            {/* У дубля депозита нет не потому, что не оплачен, а потому,
                                что он засчитан основной сделке — так и пишем. */}
                            {r.duplicate_of !== null
                              ? `${t(TX.duplicateOf)} #${r.duplicate_of}`
                              : r.deposit_at !== null
                                ? `${t(TX.depositOn)} ${r.deposit_at}`
                                : t(TX.depositNotConfirmed)}
                            {r.booked_at !== null && ` · ${t(TX.bookedOn)} ${r.booked_at}`}
                          </div>
                        )}
                      </td>
                      <td className="nowrap">{r.created}</td>
                      <td>{r.park_name ?? "—"}</td>
                      <td>{r.manager_name ?? "—"}</td>
                      <td className="nowrap">
                        {partyBasis && r.party_date !== r.date_of_event ? (
                          <>
                            {r.party_date ?? "—"}
                            <div className="sub">
                              {t(TX.inCrm)} {r.date_of_event ?? "—"}
                            </div>
                          </>
                        ) : (
                          (r.date_of_event ?? "—")
                        )}
                      </td>
                      <td className="num">
                        {partyBasis && r.party_amount_aed_minor !== r.opportunity_aed_minor ? (
                          <>
                            {r.party_amount_aed_minor === null
                              ? "—"
                              : money(r.party_amount_aed_minor, "AED", { withCode: false })}
                            <div className="sub">
                              {t(TX.inCrm)}{" "}
                              {r.opportunity_aed_minor === null
                                ? "—"
                                : money(r.opportunity_aed_minor, "AED", { withCode: false })}
                            </div>
                          </>
                        ) : r.opportunity_aed_minor === null ? (
                          "—"
                        ) : (
                          money(r.opportunity_aed_minor, "AED", { withCode: false })
                        )}
                        {/* Своя валюта подписана, только если она не AED: иначе
                            в каждой строке стояло бы одно и то же число дважды. */}
                        {r.currency !== null && r.currency !== "AED" && (
                          <div className="sub">
                            {money(r.opportunity_minor, r.currency, { withCode: true })}
                          </div>
                        )}
                      </td>
                      <td className="num">
                        {r.deposit_aed_minor === null
                          ? "—"
                          : money(r.deposit_aed_minor, "AED", { withCode: false })}
                        {/* Поле CRM пустое, депозит взят из оплаты брони — подписано,
                            чтобы число не выдавали за записанное менеджером. */}
                        {r.deposit_from_booking === true && (
                          <div className="sub">{t(TX.depositFromBooking)}</div>
                        )}
                      </td>
                      <td className="num">
                        {r.balance_aed_minor === null
                          ? "—"
                          : money(r.balance_aed_minor, "AED", { withCode: false })}
                      </td>
                      <td>
                        {r.issue_flags.length === 0 && r.exception_kinds.length === 0
                          ? "—"
                          : r.issue_flags.map((flag) => (
                              <span key={flag} className="tag tag-warn" style={{ marginRight: 4 }}>
                                {dbLabel(ISSUE_LABEL, flag, flag)}
                              </span>
                            ))}
                        {/* Исключения сверки — другой цвет: это не недочёт сделки,
                            а то, как её посчитала аналитика. */}
                        {r.exception_kinds.map((kind) => (
                          <span key={kind} className="tag" style={{ marginRight: 4 }}>
                            {dbLabel(EXCEPTION_LABEL, kind, kind)}
                          </span>
                        ))}
                      </td>
                      <td>
                        {r.lead_id === null ? (
                          <span className="sub">{t(TX.noLead)}</span>
                        ) : (
                          <a href={r.lead_bitrix_url ?? "#"} target="_blank" rel="noreferrer">
                            {r.lead_title === null || r.lead_title === ""
                              ? `#${r.lead_id}`
                              : r.lead_title}
                          </a>
                        )}
                        {r.lead_phone_e164 !== null && <div className="sub">{r.lead_phone_e164}</div>}
                      </td>
                      <td>
                        {r.stage_name ?? "—"}
                        {/* Стадия-близнец: карточка печатает настоящее имя (в
                            «нужен возврат» менеджеру нужно действие), а рядом
                            стоит то, под которым её посчитала плитка. */}
                        {r.report_stage_name !== null &&
                          r.report_stage_name !== r.stage_name && (
                            <div className="sub">
                              {t(TX.reportsAs)} {r.report_stage_name}
                            </div>
                          )}
                      </td>
                      <td className="nowrap">
                        <a className="btn-ghost" href={r.bitrix_url} target="_blank" rel="noreferrer">
                          Bitrix ↗
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/*
           * Листалка. Заказчик просил её прямо: «если выбран большой период и
           * список получается большой — сделать пагинацию». Кнопок «назад» и
           * «далее» для этого мало: сделок в базе 3 299, и по 200 это 17
           * страниц, до последней — шестнадцать нажатий. Поэтому есть ввод
           * номера и возврат в начало.
           */}
          {!onMonths && total > rows.length && (
            <div className="drawer-foot">
              <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={offset === 0 || loading}
                  onClick={() => setOffset(0)}
                >
                  {t(TX.firstPage)}
                </button>
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={offset === 0 || loading}
                  onClick={() => setOffset(Math.max(0, offset - PAGE))}
                >
                  {t(TX.previousPage)}
                </button>
              </span>

              <span className="sub" style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {num(offset + 1)}–{num(shown)} {t(UI.of)} {num(total)}
                <label style={{ display: "flex", gap: 6, alignItems: "center", marginLeft: 6 }}>
                  {t(TX.pageOf)}
                  <input
                    type="text"
                    inputMode="numeric"
                    aria-label={t(TX.goToPage)}
                    title={t(TX.goToPage)}
                    /* Пустое поле показывает НОМЕР ТЕКУЩЕЙ страницы, а набранное
                       — то, что набрали: иначе цифра прыгала бы под пальцами. */
                    value={typed === "" ? String(current) : typed}
                    onChange={(e) => setTyped(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") jump();
                    }}
                    onBlur={jump}
                    style={{ width: 54, textAlign: "center" }}
                  />
                  {t(UI.of)} {num(pages)}
                </label>
              </span>

              <button
                type="button"
                className="btn-ghost"
                disabled={shown >= total || loading}
                onClick={() => setOffset(offset + PAGE)}
              >
                {t(TX.nextPage)}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
